import React, { useState, ReactNode } from 'react'
import { NavigateFn } from '@reach/router'
import { Classes, Menu, MenuItem } from '@blueprintjs/core'
import {
  Popover,
  Icon,
  Button,
  Card,
  Flex,
  Avatar,
  IconButton,
  EmployeeSpaceAssignment,
  IGridTableColumn,
  NewGridTable,
  GridTableStatusEnum,
} from '@ui'
import track from '@track'
import Event from '@track/Event'
import { UseEmployeesHook, Filters } from './hooks/useEmployees'
import { get } from 'lodash'
import numeral from 'numeral'
import EmployeeWithDetailsPopover from '@ui/EmployeeWithDetailsPopover'
import FiltersComponent from './FiltersComponent'
import RemoteToggle from './RemoteToggle'
import NewEmployeeDialog from './NewEmployeeDialog'
import ExportEmployeesDialog from './ExportEmployeesDialog'
import UpdateRoleButton from './UpdateRoleButton'
import MultiDeleteButton from './MultiDeleteButton'
import Permissions from '@permissions/Permissions'
import { PermissionsType, IPermissionsOnEmployeeList } from '@permissions/types'
import { IEmployeeTableRecord } from '@api'
import classnames from 'classnames'

interface TableViewProps {
  employees: UseEmployeesHook
  navigate?: NavigateFn
}

export default function TableView({ employees, navigate }: TableViewProps) {
  const [isOpen, setIsOpen] = useState(false)

  const columns: IGridTableColumn<IEmployeeTableRecord>[] = [
    {
      label: 'Employee',
      sort: 'name',
      Cell: ({ cell, skeleton }) => {
        if (!cell)
          return <div className={classnames({ [Classes.SKELETON]: skeleton })}>placeholder</div>

        //todo make skeleton card
        return (
          <EmployeeCard
            employee={cell.data}
            onClick={() => {
              if (navigate) {
                track.employees({ eventName: Event.ROW_CLICK })
                navigate(`?employeeId=${cell.data.id}`)
              }
            }}
          />
        )
      },
    },
    {
      label: 'Department/Team',
      sort: 'department',
      Cell: ({ cell, skeleton }) => {
        if (!cell)
          return <div className={classnames({ [Classes.SKELETON]: skeleton })}>placeholder</div>

        let label = ''
        const dept = get(cell.data, 'dept.name')
        const team = get(cell.data, 'team.name')

        if (!dept) {
          return <div>-</div>
        } else {
          label = team ? `${dept}/${team}` : dept
        }

        return <div>{label}</div>
      },
    },
    {
      label: 'Type',
      sort: 'employment_type',
      Cell: ({ cell, skeleton }) => {
        return (
          <div className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? cell.data.employmentType : 'placehold'}
          </div>
        )
      },
    },
    {
      label: 'Manager',
      sort: 'manager',
      Cell: ({ cell, skeleton }) => {
        if (!cell)
          return (
            <div
              className={classnames({
                [Classes.SKELETON]: skeleton,
              })}>
              placeholder
            </div>
          )

        //todo make skeleton card
        return <ManagerCard employee={cell.data} />
      },
    },
    {
      label: 'HR Location',
      sort: 'geo_location',
      Cell: ({ cell, skeleton }) => (
        <div className={classnames({ [Classes.SKELETON]: skeleton })}>
          {cell ? get(cell.data, 'geoLocation.location', '-') : 'placeholder'}
        </div>
      ),
    },
    {
      label: 'Space',
      sort: 'space',
      Cell: ({ cell, skeleton }) => {
        if (!cell)
          return (
            <div
              className={classnames({
                [Classes.SKELETON]: skeleton,
              })}>
              placeholder
            </div>
          )

        //todo make skeleton card
        return <EmployeeSpaceAssignment {...cell.data} />
      },
    },
  ]

  const formatEmployees = (items: IEmployeeTableRecord[]) => {
    return items.map((b) => ({
      data: b,
    }))
  }

  const formatedEmployees = formatEmployees(employees.items)

  return (
    <Card>
      <Card.Header>
        <Toolbar
          {...{
            navigate,
            employees,
            showNewEmployeeDialog: () => setIsOpen(true),
          }}
        />
      </Card.Header>
      <NewEmployeeDialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        reload={employees.reload}
      />
      <NewGridTable<IEmployeeTableRecord>
        status={
          employees.isLoading
            ? GridTableStatusEnum.LOADING
            : employees.isPaging
            ? GridTableStatusEnum.PAGING
            : employees.isSorting
            ? GridTableStatusEnum.SORTING
            : GridTableStatusEnum.IDLE
        }
        columns={columns}
        data={formatedEmployees}
        selection={employees.selection}
        sort={{
          ...employees.sort,
          onSort: (column) => {
            employees.selection.clear()
            employees.sortBy(column)
            track.employees({
              eventName: Event.SORT,
              props: { columnName: column },
            })
          },
        }}
        page={{
          hasNextPage: employees.pageInfo.hasNextPage,
          endCursor: employees.pageInfo.endCursor,
          onNextPage: () => {
            employees.nextPage()
            track.employees({
              eventName: Event.PAGE,
            })
          },
        }}
        skeletonSize={3}
        track={{
          select: () =>
            track.employees({
              eventName: Event.ROW_SELECT,
            }),
          selectAll: () =>
            track.employees({
              eventName: Event.SELECT_ALL,
            }),
        }}
        headerTop={52}
      />
    </Card>
  )
}

function getFilterCount(filters: Filters) {
  let count = 0

  if (filters.cities.length) {
    count++
  }

  count += filters.valueFilter.length
  count += Object.values(filters.keyValueFilters).filter((v) => !!v).length
  count += Object.values(filters.filters).filter((v) => !!v).length

  return count
}

function hasQueryFilter(filters: Filters) {
  return !!filters.filters.query
}

function Toolbar({
  employees,
  showNewEmployeeDialog,
  navigate,
}: {
  employees: UseEmployeesHook
  showNewEmployeeDialog: () => void
  navigate?: NavigateFn
}) {
  const [isOpen, setIsOpen] = useState(false)
  const filterCount = getFilterCount(employees.filters)
  let helperText = ''
  let clearButton: ReactNode = null
  const selectedEmployees = employees.items.filter((e) => employees.selection.items[e.id])
  let isLocated = false
  if (selectedEmployees.length) {
    for (const emp of selectedEmployees) {
      const secondary = emp.secondarySpaceAssignments.length > 0
      const neighborhoods = emp.neighborhoods.length > 0
      const emplSpace =
        get(emp, 'spaceAssignment.space') ||
        (secondary ? 'true' : null) ||
        (neighborhoods ? 'true' : null)
      isLocated = emplSpace ? true : false
      if (isLocated) break
    }
  }

  if (!employees.isLoading || employees.isSorting || employees.isPaging) {
    if (employees.selection.count) {
      helperText = `${employees.selection.count} selected`
      clearButton = (
        <IconButton
          minimal
          tooltip="Clear selection"
          iconName="times"
          style={{ marginLeft: 4 }}
          data-button="clear_selection"
          onClick={() => {
            employees.selection.clear()
            track.employees({
              eventName: Event.CLEAR_SELECTION,
            })
          }}
        />
      )
    } else if (filterCount) {
      helperText = `${numeral(employees.count).format('0,0')} filtered record${
        employees.count === 1 ? '' : 's'
      }`

      clearButton = (
        <IconButton
          minimal
          tooltip="Clear filters"
          iconName="times"
          style={{ marginLeft: 4 }}
          onClick={() => {
            employees.clearFilters()
            track.employees({
              eventName: Event.CLEAR_ALL_FILTERS,
            })
          }}
        />
      )
    } else {
      helperText = helperText = `${numeral(employees.count).format('0,0')} record${
        employees.count === 1 ? '' : 's'
      }`
    }
  }

  return (
    <Permissions type={PermissionsType.EMPLOYEE_LIST} on={selectedEmployees}>
      {(permissions) => {
        const { canChangeRole, canToggleRemote, canDelete, canImport } =
          permissions as IPermissionsOnEmployeeList

        return (
          <Flex align="center" justify="space-between" style={{ width: '98%' }}>
            <Flex align="center">
              {employees.selection.count ? (
                <>
                  <UpdateRoleButton
                    disabled={!canChangeRole}
                    selection={employees.selection}
                    reload={employees.reload}
                  />
                  <div style={{ marginLeft: 8 }} />
                  <RemoteToggle
                    disabled={!canToggleRemote || isLocated}
                    reload={employees.reload}
                    context="remote"
                    selection={employees.selection}
                  />
                  <div style={{ marginLeft: 8 }} />
                  <RemoteToggle
                    disabled={!canToggleRemote || isLocated}
                    reload={employees.reload}
                    context="noDeskNeeded"
                    selection={employees.selection}
                  />
                  <div style={{ marginLeft: 8 }} />
                  <MultiDeleteButton
                    disabled={!canDelete}
                    selectedEmployees={selectedEmployees}
                    selection={employees.selection}
                    reload={employees.reload}
                  />
                </>
              ) : (
                <FiltersComponent
                  filterCount={
                    // Do not count `query` filter
                    filterCount - (hasQueryFilter(employees.filters) ? 1 : 0)
                  }
                  filters={employees.filters}
                  applyFilters={employees.applyFilters}
                />
              )}
              <Flex align="baseline" style={{ marginLeft: 10 }}>
                {helperText}
                {clearButton}
              </Flex>
            </Flex>
            <Flex>
              <Button
                data-button="new_employee"
                iconName="plus"
                text="New Employee"
                onClick={() => {
                  showNewEmployeeDialog()
                  track.employees({
                    eventName: Event.NEW_RECORD_DIALOG,
                  })
                }}
              />
              <Popover
                content={
                  <Menu>
                    {canImport && (
                      <>
                        <MenuItem
                          text="Import"
                          onClick={() => {
                            navigate?.('?newItem=employeeImport')

                            track.employeeImportDialog({
                              eventName: Event.NEW_RECORD_DIALOG,
                            })
                          }}
                          icon={<Icon style={{ marginTop: 2, width: 22 }} name="cloud-upload" />}
                        />
                        <MenuItem
                          text="Import History"
                          onClick={() => {
                            navigate?.('/employees/history')

                            track.employees({
                              eventName: Event.NAVIGATE,
                              props: {
                                pageName: '/employees/history',
                              },
                            })
                          }}
                          icon={<Icon style={{ marginTop: 2, width: 22 }} name="history" />}
                        />
                      </>
                    )}
                    <MenuItem
                      text="Export to CSV"
                      onClick={() => {
                        setIsOpen(true)

                        track.employees({
                          eventName: Event.EXPORT,
                        })
                      }}
                      icon={<Icon style={{ marginTop: 2, width: 22 }} name="download" />}
                    />
                  </Menu>
                }>
                <IconButton
                  round={false}
                  data-button="more_actions"
                  className="ml2"
                  iconName="ellipsis-h"
                  tooltip="More actions"
                />
              </Popover>
              {isOpen && (
                <ExportEmployeesDialog
                  isOpen
                  employees={employees}
                  onClose={() => setIsOpen(false)}
                />
              )}
            </Flex>
          </Flex>
        )
      }}
    </Permissions>
  )
}

function EmployeeCard({
  employee,
  onClick,
}: {
  employee: IEmployeeTableRecord
  onClick: () => void
}) {
  return (
    <div className="flex items-center" style={{ width: '100%' }}>
      <Avatar name={employee.name} assets={employee.assets} size={Avatar.Size.SM} />
      <div style={{ marginLeft: 14, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <div style={{ fontSize: 16 }} className="table__link" onClick={onClick}>
          {/* <Link to={employee.id} className="table__link"></Link> */}
          {/* TODO: replace fake link with Link */}
          {employee.name}
        </div>
        <div style={{ color: 'var(--grey)' }}>{employee.title}</div>
      </div>
    </div>
  )
}

function ManagerCard({ employee }: { employee: IEmployeeTableRecord }) {
  return employee.manager ? (
    <EmployeeWithDetailsPopover
      buttonProps={{ minimal: true, small: true }}
      employee={employee.manager}
    />
  ) : (
    <span>-</span>
  )
}
